import emailjs from '@emailjs/browser';

// Environment detection - set to true for production, false for development
const liveServerStatus = process.env.NODE_ENV === 'production';

// Initialize EmailJS with public key
const initEmailJS = () => {
  emailjs.init(process.env.REACT_APP_EMAILJS_PUBLIC_KEY);
};

// Initialize EmailJS on module load
initEmailJS();

/**
 * Process email subject based on server environment
 * Adds "[TEST]" prefix to subjects when not in live environment
 * @param {string} subject - The original email subject
 * @returns {string} - The processed email subject
 */
const processEmailSubject = (subject) => {
  return liveServerStatus ? subject : `[TEST] ${subject}`;
};

/**
 * Send an email using EmailJS
 * @param {Object} params - Email parameters including mailSubject, mailBody, mailOne (recipient), etc.
 * @param {string} templateId - The EmailJS template ID to use (defaults to env variable)
 * @returns {Promise} - The EmailJS send result
 */
export const sendEmail = async (params, templateId = process.env.REACT_APP_EMAILJS_TEMPLATE_ID) => {
  try {
    console.log('📧 [SendEmail] Starting email send process...');
    console.log('📧 [SendEmail] Environment check:', {
      liveServerStatus,
      hasServiceId: !!process.env.REACT_APP_EMAILJS_SERVICE_ID,
      hasTemplateId: !!templateId,
      hasPublicKey: !!process.env.REACT_APP_EMAILJS_PUBLIC_KEY
    });

    // Process subject based on server environment
    const processedSubject = processEmailSubject(params.mailSubject);
    
    const templateParams = {
      ...params,
      mailSubject: processedSubject,
      reply_to: params.reply_to || 'bodymirror@myelinz.com',
      mailTwo: process.env.REACT_APP_MAIL_TWO,
      mailThree: process.env.REACT_APP_MAIL_THREE,
      mailFour: process.env.REACT_APP_MAIL_FOUR
    };

    console.log('📧 [SendEmail] Template parameters prepared:', {
      to: templateParams.mailOne,
      subject: processedSubject,
      serviceId: process.env.REACT_APP_EMAILJS_SERVICE_ID,
      templateId: templateId,
      isTestEnvironment: !liveServerStatus
    });

    // Log email in development/test environment
    if (!liveServerStatus) {
      console.log('📧 [SendEmail] Sending test email:', {
        to: templateParams.mailOne,
        subject: processedSubject,
        isTestEnvironment: true
      });
    } else {
      console.log('📧 [SendEmail] Sending live email to:', templateParams.mailOne);
    }

    console.log('📧 [SendEmail] Calling EmailJS send...');
    const result = await emailjs.send(
      process.env.REACT_APP_EMAILJS_SERVICE_ID,
      templateId,
      templateParams
    );
    
    console.log('✅ [SendEmail] EmailJS send successful!');
    console.log('📧 [SendEmail] EmailJS result:', result);
    
    return { success: true, result };
  } catch (error) {
    console.error('❌ [SendEmail] Email sending failed:', error);
    console.error('📧 [SendEmail] Error details:', {
      name: error.name,
      message: error.message,
      status: error.status,
      text: error.text
    });
    return { success: false, error };
  }
};

/**
 * Send subscription thank you email
 * @param {string} email - The subscriber's email address
 * @returns {Promise} - The email send result
 */
export const sendSubscriptionThankYouEmail = async (email) => {
  const { getSubscriptionThankYouEmailTemplate } = await import('./templates/subscriptionThanksEmail');
  
  const emailParams = {
    mailOne: email,
    mailSubject: 'You\'re In — Welcome to the BodyMirror Enthusiasts List 🧠💪',
    mailBody: getSubscriptionThankYouEmailTemplate(email),
    subscriber_email: email,
    company_name: 'MyelinZ',
    product_name: 'BodyMirror'
  };

  return await sendEmail(emailParams, process.env.REACT_APP_EMAILJS_SUBSCRIPTION_TEMPLATE_ID);
};

/**
 * Send BodyMirror popup subscription thank you email
 * @param {string} email - The subscriber's email address
 * @returns {Promise} - The email send result
 */
export const sendBodyMirrorPopupThankYouEmail = async (email) => {
  const { getBodyMirrorPopupThankYouTemplate } = await import('./templates/bodyMirrorPopupThanksEmail');
  
  const emailParams = {
    mailOne: email,
    mailSubject: 'Welcome to the Future of Brain Health — BodyMirror is Coming! 🧠🚀',
    mailBody: getBodyMirrorPopupThankYouTemplate(email),
    subscriber_email: email,
    company_name: 'MyelinZ',
    product_name: 'BodyMirror'
  };

  return await sendEmail(emailParams, process.env.REACT_APP_EMAILJS_SUBSCRIPTION_TEMPLATE_ID);
};

/**
 * Send payment confirmation email
 * @param {Object} orderData - Order and payment information
 * @returns {Promise} - The email send result
 */
export const sendPaymentConfirmationEmail = async (orderData) => {
  try {
    console.log('📧 [EmailService] Starting payment confirmation email...');
    console.log('📧 [EmailService] Order data received:', {
      customerEmail: orderData.customerEmail,
      customerName: orderData.customerName,
      productName: orderData.productName,
      invoiceId: orderData.invoiceId,
      amount: orderData.amount
    });

    const { getPaymentConfirmationEmailTemplate } = await import('./templates/paymentConfirmationEmail');
    
    console.log('📧 [EmailService] Email template loaded successfully');
    
    const emailParams = {
      mailOne: orderData.customerEmail,
      mailSubject: `Payment Confirmed - Your ${orderData.productName} Order #${orderData.invoiceId}`,
      mailBody: getPaymentConfirmationEmailTemplate(orderData),
      customer_name: orderData.customerName,
      customer_email: orderData.customerEmail,
      invoice_id: orderData.invoiceId,
      payment_id: orderData.paymentIntentId,
      product_name: orderData.productName,
      total_amount: orderData.total || orderData.amount,
      currency: orderData.currency,
      company_name: 'MyelinZ'
    };

    console.log('📧 [EmailService] Email parameters prepared:', {
      to: emailParams.mailOne,
      subject: emailParams.mailSubject,
      templateUsed: process.env.REACT_APP_EMAILJS_PAYMENT_TEMPLATE_ID || process.env.NEXT_PUBLIC_EMAILJS_TEMPLATE_ID || 'default'
    });

    const result = await sendEmail(emailParams, process.env.REACT_APP_EMAILJS_PAYMENT_TEMPLATE_ID || process.env.NEXT_PUBLIC_EMAILJS_TEMPLATE_ID);
    
    if (result.success) {
      console.log('✅ [EmailService] Payment confirmation email sent successfully!');
      console.log('📧 [EmailService] EmailJS response:', result.result);
    } else {
      console.error('❌ [EmailService] Payment confirmation email failed:', result.error);
    }
    
    return result;
    
  } catch (error) {
    console.error('❌ [EmailService] Payment email error:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Send bundle purchase confirmation email
 * @param {Object} orderData - Bundle order information
 * @returns {Promise} - The email send result
 */
export const sendBundleConfirmationEmail = async (orderData) => {
  try {
    console.log('📧 [EmailService] Starting bundle confirmation email...');
    console.log('📧 [EmailService] Bundle order data received:', {
      customerEmail: orderData.customerEmail,
      customerName: orderData.customerName,
      invoiceId: orderData.invoiceId,
      amount: orderData.amount,
      savings: orderData.savings
    });

    const { getBundleConfirmationEmailTemplate } = await import('./templates/paymentConfirmationEmail');
    
    console.log('📧 [EmailService] Bundle email template loaded successfully');
    
    const emailParams = {
      mailOne: orderData.customerEmail,
      mailSubject: `🎉 Bundle Order Confirmed - Complete BodyMirror Package #${orderData.invoiceId}`,
      mailBody: getBundleConfirmationEmailTemplate(orderData),
      customer_name: orderData.customerName,
      customer_email: orderData.customerEmail,
      invoice_id: orderData.invoiceId,
      payment_id: orderData.paymentIntentId,
      product_name: 'Complete Bundle',
      total_amount: orderData.total || orderData.amount,
      currency: orderData.currency,
      bundle_savings: orderData.savings || 0,
      company_name: 'MyelinZ'
    };

    console.log('📧 [EmailService] Bundle email parameters prepared:', {
      to: emailParams.mailOne,
      subject: emailParams.mailSubject,
      templateUsed: process.env.REACT_APP_EMAILJS_PAYMENT_TEMPLATE_ID || process.env.NEXT_PUBLIC_EMAILJS_TEMPLATE_ID || 'default'
    });

    const result = await sendEmail(emailParams, process.env.REACT_APP_EMAILJS_PAYMENT_TEMPLATE_ID || process.env.NEXT_PUBLIC_EMAILJS_TEMPLATE_ID);
    
    if (result.success) {
      console.log('✅ [EmailService] Bundle confirmation email sent successfully!');
      console.log('📧 [EmailService] EmailJS response:', result.result);
    } else {
      console.error('❌ [EmailService] Bundle confirmation email failed:', result.error);
    }
    
    return result;
    
  } catch (error) {
    console.error('❌ [EmailService] Bundle email error:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Send payment failure notification email
 * @param {Object} orderData - Failed order information
 * @returns {Promise} - The email send result
 */
export const sendPaymentFailureEmail = async (orderData) => {
  const emailParams = {
    mailOne: orderData.customerEmail,
    mailSubject: `Payment Issue - ${orderData.productName} Order #${orderData.invoiceId}`,
    mailBody: `
    <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto; padding: 20px; background-color: #ffffff; border: 2px solid #dc2626;">
      <div style="background-color: #dc2626; color: #ffffff; padding: 30px; text-align: center;">
        <h2 style="margin: 0; font-size: 24px;">Payment Issue</h2>
        <p style="margin: 10px 0 0 0;">We encountered a problem processing your payment</p>
      </div>
      
      <div style="padding: 30px;">
        <p style="margin-bottom: 20px;">Dear ${orderData.customerName},</p>
        <p style="margin-bottom: 20px;">We encountered an issue processing your payment for <strong>${orderData.productName}</strong>.</p>
        
        <div style="background-color: #f8f8f8; padding: 20px; border: 1px solid #e5e5e5; margin: 20px 0;">
          <p style="margin-bottom: 10px;"><strong>Order ID:</strong> ${orderData.invoiceId}</p>
          <p style="margin-bottom: 10px;"><strong>Amount:</strong> ${orderData.currency} ${(orderData.amount / 100).toFixed(2)}</p>
          <p style="margin: 0;"><strong>Error:</strong> ${orderData.errorMessage || 'Payment processing failed'}</p>
        </div>
        
        <p style="margin-bottom: 20px;">Please try again or contact our support team for assistance.</p>
        
        <div style="text-align: center; margin: 30px 0;">
          <a href="https://shop.myelinz.com" style="display: inline-block; background-color: #000000; color: #ffffff; padding: 15px 30px; text-decoration: none; font-weight: 700; border: 2px solid #000000;">
            Try Again
          </a>
        </div>
        
        <p style="margin-bottom: 10px;">Need help? Contact us:</p>
        <p style="margin-bottom: 5px;">📧 Email: <a href="mailto:support@myelinz.com" style="color: #0066cc;">support@myelinz.com</a></p>
        <p style="margin-bottom: 20px;">🌐 Website: <a href="https://www.myelinz.com" style="color: #0066cc;">www.myelinz.com</a></p>
        
        <p>Best regards,<br><strong>The MyelinZ Team</strong></p>
      </div>
      
      <div style="background-color: #000000; color: #ffffff; padding: 20px; text-align: center;">
        <p style="margin: 0; font-size: 14px;">©2025 MyelinZ, All rights reserved.</p>
      </div>
    </div>
    `,
    customer_name: orderData.customerName,
    invoice_id: orderData.invoiceId,
    company_name: 'MyelinZ'
  };

  return await sendEmail(emailParams);
};
